import type {
  MessageRecord,
  ToolCallInvocationRecord,
  ToolCallRecord,
  ToolCallResponseRecord,
  TurnResponse,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import type {RuntimeEvent} from './events';

export interface TurnEventExtractorOptions {
  /** skipUserMessages defines whether to drop user message events. This is
   * useful if, for example, the frontend adds them optimistically to its store.
   */
  skipUserMessages?: boolean;
}

/**
 * TurnEventExtractor incrementally extracts {@link RuntimeEvent}s from
 * `TurnResponse` poll snapshots (responses from the WBAgent backend runtime).
 * Maintains cursors so each call to {@link extract} returns only new events
 * since the previous call.
 */
export class TurnEventExtractor {
  private groupIndex = 0;
  private lastSeenMessageIndex = 0;
  private lastSeenToolCallIndex = 0;
  private lastWasTool = false;

  /** toolCallGroupMap maps tool call IDs to their message group. */
  private toolCallGroupMap = new Map<string, string>();

  constructor(
    private readonly turnId: string,
    private readonly opts?: TurnEventExtractorOptions
  ) {}

  /**
   * extract processes the latest turn snapshot and returns events for new data
   * since the last call.
   */
  extract(turn: TurnResponse): RuntimeEvent[] {
    const events: RuntimeEvent[] = [];

    // NOTE: Call `extractMessages` first - tool calls must be extracted from messages.
    this.extractMessages(turn.messages ?? [], events);
    this.extractToolCalls(turn.tool_calls ?? [], events);

    return events;
  }

  private contentTriplet<P extends 'text-message' | 'reasoning'>(
    prefix: P,
    messageId: string,
    content: string
  ): [
    {type: `${P}-start`; messageId: string},
    {type: `${P}-content`; messageId: string; delta: string},
    {type: `${P}-end`; messageId: string},
  ] {
    return [
      {type: `${prefix}-start`, messageId},
      {type: `${prefix}-content`, messageId, delta: content},
      {type: `${prefix}-end`, messageId},
    ];
  }

  // NOTE: The WBAgent runtime does not define IDs for each individual message,
  // just the turn. The runtime requires an ID, so uniquely create one.
  private get currentMessageId(): string {
    return `${this.turnId}:group:${this.groupIndex}`;
  }

  private extractMessages(
    messages: MessageRecord[],
    events: RuntimeEvent[]
  ): void {
    for (let i = this.lastSeenMessageIndex; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.role == null) {
        continue;
      }

      // A "group" is one LLM iteration (messages between tool-result
      // boundaries). Groups provide synthetic per-message IDs since the
      // backend only assigns turn-level IDs. Messages with role "tool"
      // carry the output returned by a tool execution and act as group
      // delimiters — the group index advances on transition back to a
      // non-tool role, because that begins the next LLM call.
      if (msg.role !== 'tool' && this.lastWasTool) {
        this.groupIndex++;
        this.lastWasTool = false;
      }

      switch (msg.role) {
        case 'user':
          if (!this.opts?.skipUserMessages && msg.content) {
            events.push(
              ...this.contentTriplet(
                'text-message',
                this.currentMessageId,
                msg.content
              )
            );
          }
          break;

        case 'assistant':
          // NOTE: `tool_calls` is the authoritative record of tool invocations
          // and results.
          if (msg.tool_calls && msg.tool_calls.length > 0) {
            // Record which group each tool call belongs to.
            const messageId = this.currentMessageId;
            for (const tc of msg.tool_calls) {
              this.toolCallGroupMap.set(tc.id, messageId);
            }
          } else if (msg.content) {
            events.push(
              ...this.contentTriplet(
                'text-message',
                this.currentMessageId,
                msg.content
              )
            );
          }
          break;

        case 'reasoning':
          if (msg.content) {
            events.push(
              ...this.contentTriplet(
                'reasoning',
                this.currentMessageId,
                msg.content
              )
            );
          }
          break;

        case 'tool':
          // Tool messages mark a group boundary; the next non-tool message
          // starts a new group (new LLM iteration). Note that this event isn't
          // used to emit tool events because the top-level `tool_calls` field
          // formats the results for easier consumption.
          this.lastWasTool = true;
          break;
      }
    }

    this.lastSeenMessageIndex = messages.length;
  }

  private extractToolCalls(
    toolCalls: ToolCallRecord[],
    events: RuntimeEvent[]
  ): void {
    for (let i = this.lastSeenToolCallIndex; i < toolCalls.length; i++) {
      const tc = toolCalls[i];

      if (tc.type === 'invocation') {
        const call = tc as ToolCallInvocationRecord;
        const messageId =
          this.toolCallGroupMap.get(call.call_id) ?? this.currentMessageId;
        events.push({
          type: 'tool-call-start',
          toolCallId: call.call_id,
          toolName: call.name,
          messageId,
        });
        const argsText =
          typeof call.arguments === 'string'
            ? call.arguments
            : JSON.stringify(call.arguments);
        events.push({
          type: 'tool-call-args',
          delta: argsText,
          toolCallId: call.call_id,
        });
        events.push({type: 'tool-call-end', toolCallId: call.call_id});
      } else if (tc.type === 'response') {
        const res = tc as ToolCallResponseRecord;
        const output =
          typeof res.output === 'string'
            ? res.output
            : res.output == null
              ? ''
              : JSON.stringify(res.output);
        events.push({
          type: 'tool-call-result',
          content: output,
          isError: res.is_error ?? false,
          toolCallId: res.call_id,
        });
      }
    }

    this.lastSeenToolCallIndex = toolCalls.length;
  }
}

/**
 * Helper function to extract turn ID based on how messageId is composed.
 * Make sure to update in sync with any changes to currentMessageId!
 */
export function extractTurnId(messageId: string): string | null {
  const idx = messageId.indexOf(':group:');
  return idx >= 0 ? messageId.slice(0, idx) : null;
}
